import Cookies from 'js-cookie';
import {slideToggle} from "../_utils";

/**
 * Options for CookieConsent
 * @typedef {Object} CookieConsentOptions
 * @property {string} cookiePrefix
 * @property {boolean} openInitial
 * @property {Object.<string, Page>} pages
 * @property {Object} buttons
 * @property {string} buttons.select
 * @property {string} buttons.acceptSelection
 * @property {string} buttons.acceptAll
 * @property {Category[]} categories
 * @property {Object} events
 * @property {string} events.ready
 * @property {string} events.showPopup
 * @property {string} events.hidePopup
 * @property {string} events.consentUpdated
 * @property {string} locationPath The location path where the cookie will be set
 */

/**
 * @typedef {Object} Category
 * @property {string} cookie
 * @property {string} headline
 * @property {boolean} mandatory
 * @property {string} text
 */

/**
 * @typedef {Object} Page
 * @property {string} headline
 * @property {string} textTop
 * @property {string} textBottom
 * @property {string[]} buttons
 * @property {boolean} categoryAccordion
 */

/**
 * @typedef {Object} Consent
 * @property {boolean} functional
 * @property {boolean} mandatory
 * @property {boolean} analyzing
 * @property {boolean} marketing
 */

/**
 * @typedef {Object} ShowPopupEventDetail
 * @property {string} [pageId]
 * @property {HTMLElement} [element]
 */

/**
 * @typedef {Object} ConsentUpdatedEventDetail
 * @property {Consent} consent
 */

/**
 * Cookie Consent Manager.
 * This is a singleton.
 */
export class CookieConsent {

    static instance = null;

    /**
     * @type {HTMLDivElement|null}
     */
    popup = null;

    /**
     * @type {Consent}
     */
    minimumConsent = {
        mandatory: true,
        functional: false,
        analyzing: false,
        marketing: false,
    };

    /**
     * @param {HTMLElement} target
     * @param {CookieConsentOptions} options
     */
    constructor(target, options = {}) {
        if (CookieConsent.instance) {
            return CookieConsent.instance;
        }

        /** @type {CookieConsentOptions} */
        const defaults = {
            cookiePrefix: 'form4-',
            openInitial: true,
            pages: {},
            buttons: {
                select: 'Auswählen',
                acceptSelection: 'Auswahl akzeptieren',
                acceptNecessary: 'Nicht notwendige ablehnen',
                rejectAll: 'Alle ablehnen',
                acceptAll: 'Alle akzeptieren',
            },
            categories: [],
            events: {
                ready: 'cookieConsentManagerReady', // returns: this.consent
                showPopup: 'cookieConsentPopupShow',
                hidePopup: 'cookieConsentPopupHide',
                consentUpdated: 'cookieConsentUpdated', // returns: this.consent
            },
            locationPath: '',
        };

        /** @type {HTMLElement} */
        this.target = target;

        /** @type {CookieConsentOptions} */
        this.options = {
            ...defaults,
            ...options
        };

        /** @type {HTMLElement} */
        this.originTrigger = null;

        /** @type {Consent|null} */
        this.consent = this._getConsentFromCookie();

        this._buildPopup();
        this._setupHandler();

        const readyEvent = new CustomEvent(this.options.events.ready, {
            detail: {
                consent: this.getConsent(),
            }
        });
        this.target.dispatchEvent(readyEvent);

        if (this.getConsent() === null && this.options.openInitial !== false) {
            this._triggerShowPopupEvent();
        }

        CookieConsent.instance = this;
    }


    /**
     * Create popup html and prepend to body
     * @private
     */
    _buildPopup() {
        let pagesHtml = '';

        /** @type {HTMLDivElement} */
        this.popup = document.createElement('div');
        this.popup.className = 'js-cookie-consent-popup-wrapper';
        this.popup.setAttribute('aria-live', 'polite');
        this.popup.setAttribute('role', 'dialog');
        this.target.prepend(this.popup);

        /** @type {[string, Page][]} */
        const pages = Object.entries(this.options.pages);
        pages.forEach(([pageId, page]) => {
            let categoryAccordion = '',
                textTop = '',
                textBottom = '',
                buttons = '';

            if (page.categoryAccordion === true) {
                categoryAccordion = this._buildCategoryAccordion();
            }

            if (page.textTop) {
                textTop = `<div class="ce-bodytext hyphenate">${page.textTop}</div>`;
            }

            if (page.textBottom) {
                textBottom = `<div class="ce-bodytext hyphenate">${page.textBottom}</div>`;
            }

            if (page.buttons) {
                page.buttons.forEach((button) => {
                    if (button === 'select') {
                        buttons += `<button class="button-link-ghost-big js-cookie-consent-button-select" type="button">${this.options.buttons.select}</button>`;
                    }

                    if (button === 'accept-necessary') {
                        buttons += `<button class="button-link-ghost-big js-cookie-consent-button-accept-necessary" type="button">${this.options.buttons.acceptNecessary}</button>`;
                    }

                    if (button === 'accept-selection') {
                        buttons += `<button class="button-link-ghost-big js-cookie-consent-button-accept-selection" type="button">${this.options.buttons.acceptSelection}</button>`;
                    }

                    if (button === 'reject-all') {
                        buttons += `<button class="button-link-ghost-big js-cookie-consent-button-reject-all" type="button">${this.options.buttons.rejectAll}</button>`;
                    }

                    if (button === 'accept-all') {
                        buttons += `<button class="button-link-big js-cookie-consent-button-accept-all" type="button">${this.options.buttons.acceptAll}</button>`;
                    }
                });
            }

            const level1Headline = page.headline ? `<h3 class="js-cookie-consent-popup-page-headline">${page.headline}</h3>` : '';

            pagesHtml += `
                    <div class="js-cookie-consent-popup-page" id="js-cookie-consent-popup-page-${pageId}">
                        <div class="js-cookie-consent-popup-page-content">
                            ${level1Headline}
                            ${textTop}
                            ${categoryAccordion}
                            ${textBottom}
                        </div>
                        <div class="js-cookie-consent-popup-page-buttons">${buttons}</div>
                    </div>
                `;
        });

        this.popup.innerHTML = `
                <div class="js-cookie-consent-popup">
                    <div class="js-cookie-consent-popup-inner">
                        ${pagesHtml}
                    </div>
                </div>
            `;
    }

    /**
     * Create and return html for category accordion
     * @returns {string}
     * @private
     */
    _buildCategoryAccordion() {
        let html = '';

        /** @param {Category} category */
        this.options.categories.forEach((category) => {
            let additionalAttributes = '',
                additionalClasses = '';

            // check the checkbox if the category is allowed
            if (this.consentHasCategory(category.cookie) === true) {
                additionalAttributes = 'checked="checked"';
            }

            // check and disable the checkbox if the category is mandatory
            if (category.mandatory === true) {
                additionalAttributes = 'checked="checked" disabled="disabled"';
                additionalClasses = ' js-cookie-consent-popup-accordion-element-mandatory';
            }

            html += `
                    <div class="js-cookie-consent-popup-accordion-element ${additionalClasses}">
                        <div class="js-cookie-consent-popup-accordion-element-header">
                            <div class="js-cookie-consent-popup-accordion-checkbox">
                                <input id="consent-checkbox-${category.cookie}"
                                       type="checkbox"
                                       value="${category.cookie}"
                                       ${additionalAttributes}
                                />
                                <label for="consent-checkbox-${category.cookie}">
                                    <span class="sr-only">${category.headline}</span>
                                </label>
                            </div>
                            <button class="js-cookie-consent-popup-accordion-element-trigger"
                                    id="trigger-${category.cookie}"
                                    aria-controls="content-${category.cookie}"
                                    aria-expanded="false"
                            >
                                ${category.headline}
                            </button>
                        </div>
                        <div class="js-cookie-consent-popup-accordion-element-content"
                             id="content-${category.cookie}"
                             aria-labelledby="trigger-${category.cookie}"
                        >
                            <div class="js-cookie-consent-popup-accordion-element-content-inner ce-bodytext hyphenate">
                                ${category.text}
                            </div>
                        </div>
                    </div>
                `;
        });

        return `<div class="js-cookie-consent-popup-accordion">${html}</div>`;
    }

    /**
     * Setup event handler
     * @private

     */
    _setupHandler() {
        const events = this.options.events;

        // global show popup event (use this event to show the popup)
        this.target.addEventListener(
            events.showPopup,
            /** @param {CustomEvent<ShowPopupEventDetail>} event */
            (event) => {
                const pageId = event.detail.pageId;
                const element = event.detail.element;

                if (element) {
                    this.originTrigger = element;
                }

                this._showPopup(pageId);
            },
        );

        // global hide popup event (use this event to hide the popup)
        this.target.addEventListener(
            events.hidePopup,
            () => {
                this._hidePopup();

                if (this.originTrigger) {
                    this.originTrigger.dispatchEvent(new Event('focus'));
                    this.originTrigger = null;
                }
            }
        );

        // global popup trigger
        this.target.addEventListener('click', (event) => {
            /** @type {HTMLElement} */
            const eventTarget = event.target;
            if (!eventTarget.classList.contains('cookie-consent-popup-trigger')) {
                return;
            }

            event.stopPropagation();
            event.preventDefault();

            const pageId = eventTarget.dataset.cookieConsentPopupPage || 'intro';
            this._triggerShowPopupEvent(eventTarget, pageId);
        });

        // switch to managing page
        this.target.querySelectorAll('.js-cookie-consent-button-select').forEach((button) => {
            /** @param {HTMLButtonElement} button */
            button.addEventListener('click', () => {
                this._showPage('manager');
            });
        });

        // accept necessary
        this.target.querySelectorAll('.js-cookie-consent-button-accept-necessary').forEach((button) => {
            /** @param {HTMLButtonElement} button */
            button.addEventListener('click', () => {
                this._updateConsent(this.minimumConsent);
                this._triggerHidePopupEvent();
            });
        });

        // reject all
        this.target.querySelectorAll('.js-cookie-consent-button-reject-all').forEach((button) => {
            /** @param {HTMLButtonElement} button */
            button.addEventListener('click', () => {
                this._updateConsent(this.minimumConsent);
                this._triggerHidePopupEvent();
            });
        });

        // accept selection
        this.target.querySelectorAll('.js-cookie-consent-button-accept-selection').forEach((button) => {
            /** @param {HTMLButtonElement} button */
            button.addEventListener('click', () => {
                this._updateConsent();
                this._triggerHidePopupEvent();
            });
        });

        // accept all
        this.target.querySelectorAll('.js-cookie-consent-button-accept-all').forEach((button) => {
            /** @param {HTMLButtonElement} button */
            button.addEventListener('click', () => {
                this.target.querySelectorAll('input[type="checkbox"]')
                    .forEach(/** @param {HTMLInputElement} checkbox */ (checkbox) => {
                        checkbox.checked = true;
                    });

                this._updateConsent();
                this._triggerHidePopupEvent();
            });
        })

        // open / close accordion event
        this.target.querySelectorAll('.js-cookie-consent-popup-accordion-element button')
            .forEach((accordionElementButton) => {
                accordionElementButton.addEventListener('click', (event) => {
                    /** @type {HTMLElement} */
                    const eventTarget = event.target;
                    const element = eventTarget.closest('.js-cookie-consent-popup-accordion-element'),
                        content = element.querySelector('.js-cookie-consent-popup-accordion-element-content'),
                        activeClass = 'js-cookie-consent-popup-accordion-element-active',
                        isActive = element.classList.contains(activeClass);

                    element.classList.toggle(activeClass, !isActive);

                    slideToggle(content, 300);

                    eventTarget.setAttribute('aria-expanded', (!isActive).toString())
                });
            })
    }

    /**
     * @param {HTMLElement} [element]
     * @param {string} [pageId]
     * @private

     */
    _triggerShowPopupEvent(element, pageId) {
        /**
         * @type {CustomEvent<ShowPopupEventDetail>}

         */
        const event = new CustomEvent(this.options.events.showPopup, {
            detail: {
                element,
                pageId
            }
        });
        this.target.dispatchEvent(event);
    }

    /**
     * Open popup
     * DON'T USE THIS FUNCTION TO SHOW POPUP (USE _triggerShowPopupEvent INSTEAD)
     * @param {string} pageId
     * @private

     */
    _showPopup(pageId) {
        pageId = (pageId) ? pageId : 'intro';

        // activate focus trap
        this._handleFocusTrap(true);

        // show target page
        this._showPage(pageId);

        // make popup visible
        this.target.classList.add('js-cookie-consent-popup-visible');

        // update page variable
        const pageEl = this.popup.querySelector(`#js-cookie-consent-popup-page-${pageId}`);

        // wait for popup transition
        setTimeout(() => {
            // set focus in popup
            const scope = pageEl || this.popup;
            this._setPopupFocus(scope);
        }, 300);
    }

    /**
     * @private

     */
    _triggerHidePopupEvent() {
        const event = new CustomEvent(this.options.events.hidePopup);
        this.target.dispatchEvent(event);
    }

    /**
     * Close popup
     * DON'T USE THIS FUNCTION TO CLOSE POPUP (USE _triggerHidePopupEvent INSTEAD)
     * @private

     */
    _hidePopup() {
        this._handleFocusTrap(false);
        this.target.classList.remove('js-cookie-consent-popup-visible');

        // wait for popup transition
        setTimeout(() => {
            if (this.originTrigger) {
                this.originTrigger.dispatchEvent(new Event('focus'));
            }
        }, 300);
    }

    /**
     * Store consent data in cookie
     * @param {Consent} data
     * @private

     */
    _setConsentCookie(data) {
        Cookies.set(this.options.cookiePrefix + 'cookie-consent', JSON.stringify(data), {
            expires: 365,
            secure: true,
            sameSite: 'strict',
            path: this.options.locationPath,
        });
    }

    /**
     *
     * @returns {Consent|null}
     * @private
     */
    _getConsentFromCookie() {
        const cookieValue = Cookies.get(this.options.cookiePrefix + 'cookie-consent');
        let parsedValue = null;

        if (cookieValue) {
            try {
                parsedValue = JSON.parse(cookieValue);
            } catch (e) {
                // Handle invalid JSON string gracefully
                console.error('Invalid JSON string in cookie:', e);
                parsedValue = null;
            }
        }

        return parsedValue;
    }

    /**
     * Get stored data from cookie
     *
     * @returns {Consent|null}
     */
    static getConsent() {
        return CookieConsent.instance ? CookieConsent.instance.getConsent() : null;
    }

    /**
     * Remove cookie
     * @private

     */
    _removeConsentCookie() {
        Cookies.remove(this.options.cookiePrefix + 'cookie-consent');
    }

    /**
     * Show specific page in popup
     * @param {string} pageId
     * @private

     */
    _showPage(pageId) {
        if (!pageId) {
            return;
        }

        const pageEl = this.target.querySelector('#js-cookie-consent-popup-page-' + pageId);

        // hide all pages
        this.target.querySelectorAll('.js-active').forEach((element) => {
            element.classList.remove('js-active');
        });

        if (pageEl !== null) {
            // show target page
            pageEl.classList.add('js-active');

            // set focus in target page
            this._setPopupFocus(pageEl);
        }
    }

    /**
     * Set consent variable
     * @param {Consent|null} newConsent
     * @private

     */
    _updateConsent(newConsent = null) {
        /** @type {NodeListOf<HTMLInputElement>} */
        const consentCheckboxes = this.target.querySelectorAll('.js-cookie-consent-popup-accordion-checkbox > input[type="checkbox"]');

        if (newConsent !== null) {
            this._setConsentCookie(newConsent);
            this.setConsent(newConsent);
            this._triggerConsentUpdatedEvent(newConsent);
            consentCheckboxes.forEach((checkbox) => {
                const value = checkbox.value;
                checkbox.checked = newConsent[value];
            });
            return;
        }

        const consent = {};

        // get settings and save them in this.consent
        this.target.querySelectorAll('.js-cookie-consent-popup-accordion-checkbox > input[type="checkbox"]').forEach(/** @param {HTMLInputElement} checkbox */ (checkbox) => {
            const value = checkbox.value;
            consent[value] = checkbox.checked;
        });

        // update cookie
        this._setConsentCookie(consent);
        // update current consent
        this.setConsent(consent);

        // trigger global updated event
        this._triggerConsentUpdatedEvent(consent);
    }

    /**
     *
     * @param {Consent} consent
     * @private

     */
    _triggerConsentUpdatedEvent(consent) {
        /**
         * @type {CustomEvent<ConsentUpdatedEventDetail>}

         */
        const event = new CustomEvent(this.options.events.consentUpdated, {
            detail: {
                consent
            },
            bubbles: true,
        });
        this.target.dispatchEvent(event);
        location.reload();
    }

    /**
     * Get current consent
     * @return {Consent|null}
     */
    getConsent() {
        return this.consent;
    }

    setConsent(consent) {
        this.consent = consent;
    }

    /**
     * Check if consent has category
     * @param category
     * @returns {null|boolean}
     */
    consentHasCategory(category) {
        if (this.getConsent() === null) {
            return false;
        }

        return this.getConsent()[category] === true;
    }

    /**
     * Set focus to first interactive (and not disabled) element in scope (scope = popup or page)
     * @param {HTMLElement} scope
     * @private

     */
    _setPopupFocus(scope) {
        scope.querySelector(':is(a, button, input):not([disabled])')
            ?.dispatchEvent(new Event('focus'));
    }

    /**
     * Activate or deactivate the focus trap (if popup is open: disable focus on elements outside the popup)
     * @param {boolean} active
     * @private

     */
    _handleFocusTrap(active) {
        document.querySelectorAll(':is(#skiplinks, #header, #page) :is(a, input, button, textarea, select)').forEach((element) => {
            if (active) {
                if (element.hasAttribute('tabindex')) {
                    // if the element has already a tabindex attribute, save the current value
                    element.dataset.tabindex = element.getAttribute('tabindex');
                }
                element.setAttribute('tabindex', -1);
            }
            else {
                const oldTabindex = element.dataset.tabindex;

                if (oldTabindex !== null && oldTabindex !== '') {
                    // recover old tabindex (before focus trap)
                    element.setAttribute('tabindex', oldTabindex);
                } else {
                    element.removeAttribute('tabindex');
                }
            }
        });
    }
}
